import { usePostUsers } from 'api/payments/generated/fragmentPaymentsAPI';
import { useToast } from 'components/ToastManager';
import React, { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';

import { FormButtons } from '@fragment/ui/src/components/Button/ButtonGroup/FormButtons';
import { ModalField } from '@fragment/ui/src/components/Modal/ModalField';
import { ModalTextInput } from '@fragment/ui/src/components/Modal/ModalTextInput';
import { Toast } from '@fragment/ui/src/components/Toast';

import { PaymentsModal } from './PaymentsModal';

type FormData = {
  externalId: string;
};

type CreateUserModalProps = {
  onClose: () => void;
  onCreated: (externalId: string) => void;
};

export const CreateUserModal = ({
  onClose,
  onCreated,
}: CreateUserModalProps) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const postUserMutation = usePostUsers();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: { externalId: '' },
  });

  const onSubmit = useCallback(
    async (formData: FormData) => {
      try {
        await postUserMutation.mutateAsync({
          data: {
            external_id: formData.externalId.trim(),
          },
        });
        await queryClient.invalidateQueries({ queryKey: ['/users'] });
        showToast(<Toast type="success" message="User created successfully" />);
        onCreated(formData.externalId.trim());
        onClose();
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to create user. Please try again.';
        showToast(
          <Toast
            type="error"
            message={`Failed to create user: ${errorMessage}`}
          />
        );
      }
    },
    [postUserMutation, queryClient, showToast, onCreated, onClose]
  );

  const canSubmit = isValid && !postUserMutation.isPending;

  return (
    <PaymentsModal
      data-testid="create-user-modal"
      onClose={onClose}
      width="sm"
      // eslint-disable-next-line @typescript-eslint/no-misused-promises -- grandfathered
      onSubmit={handleSubmit(onSubmit)}
      header={<div className="text-main">Create User</div>}
      body={
        <div className="flex flex-col space-y-f2">
          <ModalField
            hasError={!!errors.externalId}
            label="External ID"
            labelWidth="w-f12"
          >
            <ModalTextInput
              data-testid="user-external-id-input"
              placeholder="External ID"
              {...register('externalId', {
                required: 'External ID is required',
                validate: (value) =>
                  value.trim() !== '' || 'External ID cannot be empty',
              })}
            />
          </ModalField>
        </div>
      }
      footer={
        <div className="flex flex-row justify-between w-full">
          <FormButtons
            type="secondary"
            cancelButtonLabel="Cancel"
            submitButtonLabel={
              postUserMutation.isPending ? 'Creating...' : 'Create'
            }
            onCancel={onClose}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises -- grandfathered
            onSubmit={handleSubmit(onSubmit)}
            disabled={!canSubmit}
          />
        </div>
      }
    />
  );
};
